import { useCallback, useEffect, useState } from 'react'
import { Box, Button, Card, Divider, Input, Modal, Stack, Typography } from '@prometheus/components'
import { guestPlayUrl } from '../utils/guestSession'
import { copyText } from '../utils/copyText'

interface GuestLinkModalProps {
  open: boolean
  /** The guest's play key. Without one there is nothing to show. */
  token?: string | null
  onClose: () => void
  /**
   * Shown automatically right after someone chooses to play as a guest, when
   * the message needs to read as "save this now", not "here it is again".
   */
  introduction?: boolean
}

/**
 * The play link: a guest's only way back to their games.
 *
 * Nothing ties a guest session to an email or a password, so this link IS the
 * account. The copy says so plainly — losing it means losing the games, and
 * anyone else holding it can play as them.
 */
export const GuestLinkModal = ({ open, token, onClose, introduction }: GuestLinkModalProps) => {
  const [copied, setCopied] = useState(false)
  const url = token ? guestPlayUrl(token) : ''

  useEffect(() => {
    if (!open) {
      setCopied(false)
    }
  }, [open])

  const onCopy = useCallback(async () => {
    setCopied(await copyText(url))
  }, [url])

  if (!open || !token) {
    return null
  }

  return (
    <Modal onClose={onClose}>
      <Card padding="none" borderColor="warning" style={{ maxWidth: '520px' }}>
        <Box space="sm">
          <Typography variant="h4" retro color="warning">
            {introduction ? 'You’re playing as a guest' : 'Your play link'}
          </Typography>
        </Box>
        <Box space="base">
          <Stack space="sm">
            <Typography variant="body2">
              Save this link. It is the only way back to your games — on this device or any other.
              Anyone who has it can play as you, so keep it to yourself.
            </Typography>
            <Input
              fullWidth
              readOnly
              aria-label="Your play link"
              value={url}
              onFocus={e => e.target.select()}
            />
            <Typography variant="caption" color="text-muted">
              Clearing this browser’s data without saving the link loses the games it opens.
              Signing in with Google instead keeps games on your account — but starts a fresh
              player, so finish this game as a guest first.
            </Typography>
          </Stack>
        </Box>
        <Divider />
        <Box space="sm">
          <div style={{ display: 'flex', gap: 'var(--prometheus-spacing-sm)', justifyContent: 'flex-end' }}>
            <Button variant={copied ? 'success' : 'primary'} outline={!copied} onClick={onCopy}>
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
            <Button variant="warning" outline onClick={onClose}>Done</Button>
          </div>
        </Box>
      </Card>
    </Modal>
  )
}
